import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { CheckCircleRounded } from "@material-ui/icons";
import LensIcon from "@material-ui/icons/Lens";
import LensOutlinedIcon from "@material-ui/icons/LensOutlined";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormCombos } from "../../../hooks/useFormCombos";
import { addAgendaAppointment } from "../../../services/meeting.service";
import { Table } from "../../Common/Table";
import Textarea from "../../Common/Textarea";
import { getCurrencySymbol } from "../../Pages/utils";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh",
  },
  spacer: {
    marginBottom: 20,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50,
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText,
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50,
  },
  titleModal: {
    color: "#031795",
    fontWeight: "bold",
    fontSize: "20px",
  },
  lgpdInfoContainer: {
    display: "flex",
    flex: 1,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    marginTop: "16px",
  },
}));

const ConfirmScheduleModal = ({
  setModalStatus,
  bodyEmail,
  meetingInfo,
  onSucces,
  formattedSlots,
  setToastMessage,
  t,
}) => {
  const classes = useStyles();
  const { statusMaterial, getStatusMaterial } = useFormCombos();

  const emailsRef = useRef();

  const [filesAnexo, setFilesAnexo] = useState([]);
  const [content, setContent] = useState(bodyEmail);
  const [isEmailsErrored, setIsEmailsErrored] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showDetailedValues, setShowDetailedValues] = useState(true);

  const columns = [
    {
      title: t("meeting.startTimeSimple").toUpperCase(),
      field: "meetStartTime",
      render: (row) =>
        `${row.meetStartTime.split(":")[0]}:${row.meetStartTime.split(":")[1]}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "4px",
        fontSize: "10px",
      },
    },
    {
      title: t("meeting.endTimeSimple").toUpperCase(),
      field: "meetEndTime",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "4px",
        fontSize: "10px",
      },
      render: (row) =>
        `${row.meetEndTime.split(":")[0]}:${row.meetEndTime.split(":")[1]}`,
    },
    {
      title: "'",
      field: "duration",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        fontSize: "10px",
        minWidth: "6px",
      },
      render: (row) => {
        const timeParts = row.duration.split(":");

        return `${Number(timeParts[0]) * 60 + Number(timeParts[1])}'`;
      },
    },
    {
      title: t("meeting.requester").toUpperCase(),
      field: "requester",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        fontSize: "10px",
        minWidth: "70px",
      },
    },
    {
      title: t("meeting.kindSlot").split(" ")[0].toUpperCase(),
      field: "slotType",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "8px",
        fontSize: "10px",
      },
      render: (row) => `${row.slotType.toUpperCase()}`,
    },
    {
      title: "S",
      field: "statusMaterial",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "8px",
        fontSize: "10px",
      },
      render: (row) => colorStatusGrid(row.statusMaterial),
    },
    {
      title: t("meeting.presenter").toUpperCase(),
      field: "presenter",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "70px",
        fontSize: "10px",
      },
    },
    {
      title: t("meeting.themeSlot").toUpperCase(),
      field: "name",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
    },
    {
      title: t("meeting.objective").toUpperCase(),
      field: "objective",
      resizable: false,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
    },
    {
      title: t("slots.companies").toUpperCase(),
      field: "companies",
      resizable: false,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
      render: (row) => row.companies.map((cp) => cp.companyName).join(", "),
    },
    {
      title: t("meeting.modality").toUpperCase(),
      field: "modalities",
      render: (row) =>
        `${row.modalities.map((m) => `${m.modalityName}`).join(", ")}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        fontSize: "10px",
      },
    },
    {
      title: `${t("budgetedValue").toUpperCase()}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
      field: "budgetedValue",
      editable: "never",
      render: (row) =>
        row.budgetedValue
          ? `${getCurrencySymbol(row.currency)}${row.budgetedValue}`
          : "",
    },
    {
      title: `${t("multiannualBudgetAmount").toUpperCase()}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
      field: "multiannualBudgetAmount",
      editable: "never",
      render: (row) =>
        row.multiannualBudgetAmount
          ? `${getCurrencySymbol(row.currency)}${row.multiannualBudgetAmount}`
          : "",
    },
    {
      title: `${t("amountNotBudgetedYear").toUpperCase()}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
      field: "amountNotBudgetedYear",
      editable: "never",
      render: (row) =>
        row.amountNotBudgetedYear
          ? `${getCurrencySymbol(row.currency)}${row.amountNotBudgetedYear}`
          : "",
    },
    {
      title: `${t("requestedValue").toUpperCase()}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
      field: "requestedValue",
      editable: "never",
      render: (row) =>
        row.requestedValue
          ? `${getCurrencySymbol(row.currency)}${row.requestedValue}`
          : "",
    },
    {
      title: `${t("value").toUpperCase()}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
      field: "value",
      editable: "never",
      render: (row) =>
        row.value ? `${getCurrencySymbol(row.currency)}${row.value}` : "",
    },
    {
      title: `${t("additive").toUpperCase()}`,
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        minWidth: "150px",
        fontSize: "10px",
      },
      field: "additive",
      editable: "never",
      render: (row) =>
        row.additive ? `${getCurrencySymbol(row.currency)}${row.additive}` : "",
    },
    {
      title: t("sponsor").toUpperCase(),
      field: "sponsor",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        fontSize: "10px",
      },
    },
    {
      title: t("slots.budget.title").toUpperCase(),
      field: "budgeted",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        fontSize: "10px",
      },
    },
    {
      title: t("slots.finalElevation").toUpperCase(),
      field: "finalAuthority",
      headerStyle: {
        fontSize: "10px",
        fontWeight: "bold",
      },
      cellStyle: {
        fontSize: "10px",
      },
    },
  ];

  const [filesUploadedWithSameNameState, setFilesUploadedWithSameName] =
    useState([]);
  const [sameFilesUploaded, setSameFilesUploaded] = useState([]);
  const [openUploadSameFileDialog, setOpenUploadSameFileDialog] =
    useState(false);

  const [fileOver, setFileOver] = useState(false);

  let bodyTimeout = null;

  useEffect(() => {
    if (localStorage.getItem("i18nextLng")) {
      getStatusMaterial(localStorage.getItem("i18nextLng"));
    }
  }, []);

  useEffect(() => {
    if (emailsRef.current && meetingInfo.meetConfirmationRecipients) {
      let initialEmails = "";

      meetingInfo.meetConfirmationRecipients.forEach((item) => {
        initialEmails += `${item};`;
      });

      emailsRef.current.value = initialEmails;
      setIsEmailsErrored(false);
    }
  }, [emailsRef, meetingInfo]);

  const handleCheckAngeda = useCallback(() => {
    const emailsRefValue = emailsRef?.current?.value;

    const formattedRecipients = emailsRefValue
      ?.split(";")
      ?.filter((e) => e.length > 5)
      ?.map((email) => ({
        address: email.replaceAll(" ", ""),
      }));

    const emailValidator =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    let isEmailsValid = true;

    formattedRecipients.forEach((email) => {
      if (!emailValidator.test(String(email.address).toLowerCase())) {
        isEmailsValid = false;
      }
    });

    if (!isEmailsValid) {
      setToastMessage({
        open: true,
        message: "Email inválido.",
        type: "error",
      });

      setLoading(false);
      return;
    }

    const formData = new FormData();

    formData.append("id", meetingInfo.meetId);
    formData.append("template", content);
    formData.append("emails", emailsRefValue);
    formData.append("DetailedValues", showDetailedValues);

    let count = 0;

    for (const file of filesAnexo) {
      formData.append(`Files[${count}].fromFiles`, file.file);
      formData.append(`Files[${count}].fileType`, file.contentType);

      count += 1;
    }

    if (filesAnexo.length < 1) {
      formData.append(`Files`, []);
    }

    if (!emailsRefValue || emailsRefValue == "") {
      setToastMessage({
        message:
          "Não é possível enviar esta reunião porque não há destinatários no campo “Para”",
        type: "error",
        open: true,
      });

      setLoading(false);
      return;
    }

    setLoading(true);

    addAgendaAppointment(meetingInfo.meetId, formData)
      .then((res) => {
        if (res.error.length > 0) {
          setToastMessage({
            message: "Erro ao tentar confirmar agenda.",
            type: "error",
            open: true,
          });

          return;
        }

        const emails = emailsRefValue.split(";");

        const { data: confirmScheduleResponse } = res.data;

        if (confirmScheduleResponse?.id?.length > 0) {
          setToastMessage({
            message:
              "Agenda confirmada com sucesso, mas arquivos excederam tamanho limite e não foram enviados",
            type: "success",
            open: true,
          });
        }

        onSucces(emails);
        setFilesAnexo([]);
        setModalStatus(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    emailsRef,
    content,
    meetingInfo,
    bodyEmail,
    filesAnexo,
    onSucces,
    showDetailedValues,
  ]);

  const colorStatusGrid = useCallback((statusMaterial) => {
    if (statusMaterial == 1) {
      return (
        <div style={{ width: "10px" }}>
          <LensIcon style={{ color: "green", fontSize: "small" }} />
        </div>
      );
    } else if (statusMaterial == 2) {
      return (
        <div style={{ width: "10px" }}>
          <LensIcon style={{ color: "gray", fontSize: "small" }} />
        </div>
      );
    } else if (statusMaterial == 3) {
      return (
        <div style={{ width: "10px" }}>
          <LensIcon style={{ color: "red", fontSize: "small" }} />
        </div>
      );
    } else if (statusMaterial == 4) {
      return (
        <div style={{ width: "10px" }}>
          <LensOutlinedIcon style={{ fontSize: "small" }} />
        </div>
      );
    }
  }, []);

  return (
    <div>
      <span
        style={{ float: "right", cursor: "pointer" }}
        onClick={() => {
          setModalStatus(false);
        }}
      >
        [X] Fechar
      </span>
      <span className={classes.titleModal}>
        {meetingInfo?.committeName}{" "}
        {meetingInfo.roundRobin
          ? `- ${meetingInfo?.meetDate} - Round Robin`
          : `- ${meetingInfo?.meetDate} -  ${meetingInfo?.meetStartTime} as ${meetingInfo?.meetEndTime}`}{" "}
      </span>
      <br />
      <br />
      <div className={classes.root}>
        <Grid container>
          <Grid item xs={12}>
            <Textarea
              name="for"
              id="for"
              label={t("forms.forLabel")}
              error={isEmailsErrored}
              textAreaRef={emailsRef}
              onChange={(event) => {
                if (!event.target.value) {
                  setIsEmailsErrored(true);
                } else if (isEmailsErrored) {
                  setIsEmailsErrored(false);
                }
              }}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "1%", width: "100%" }}>
            <TextField
              name="subject"
              id="subject"
              label={t("meeting.subject")}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              required
              autoComplete="nope"
              disabled={true}
              value={
                meetingInfo
                  ? `${meetingInfo?.committeName} - ${
                      meetingInfo.roundRobin
                        ? `${meetingInfo?.meetDate} - Round Robin`
                        : `${meetingInfo?.meetDate} -  ${meetingInfo?.meetStartTime} as ${meetingInfo?.meetEndTime}`
                    } `
                  : ""
              }
            />
          </Grid>
        </Grid>

        <CKEditor
          placeholder={t("forms.bodyEmailPlaceholder")}
          editor={ClassicEditor}
          config={{
            toolbar: [
              "heading",
              "|",
              "bold",
              "italic",
              "bulletedList",
              "numberedList",
            ],
          }}
          data={content}
          onChange={(event, editor) => {
            if (bodyTimeout) {
              clearTimeout(bodyTimeout);
            }

            bodyTimeout = setTimeout(() => {
              setContent(editor.getData());
            }, 300);
          }}
          onBlur={(event, editor) => {
            setContent(editor.getData());
          }}
        />
      </div>

      <div style={{ padding: "16px" }}>
        <Table
          data={
            meetingInfo.roundRobin
              ? formattedSlots
              : formattedSlots?.filter(
                  (slot) =>
                    slot.statusSlot === "P" ||
                    slot.statusSlot === "C" ||
                    slot.statusSlot === "F"
                )
          }
          columns={meetingInfo.roundRobin ? columns.slice(3) : columns}
        />
      </div>

      <Grid item xs={6} style={{ marginTop: "16px", padding: "16px" }}>
        <FormControlLabel
          fullWidth
          style={{ fontSize: "20px", fontWeight: "bold" }}
          control={
            <Checkbox
              value={showDetailedValues}
              checked={showDetailedValues}
              onChange={() => setShowDetailedValues(!showDetailedValues)}
              name="showDetailedValues"
              id="showDetailedValues"
              color="primary"
            />
          }
          label={t("meeting.showDetailedValues").toUpperCase()}
        />
      </Grid>

      <Grid container direction="row" justify="center" alignItems="center">
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            style={{ backgroundColor: "#40916c", minWidth: "230px" }}
            className={classes.button}
            variant="contained"
            startIcon={<CheckCircleRounded style={{ fontSize: 30 }} />}
            onClick={handleCheckAngeda}
            disabled={
              meetingInfo.roundRobin
                ? formattedSlots.length < 1
                : formattedSlots?.filter(
                    (slot) =>
                      slot.statusSlot === "P" ||
                      slot.statusSlot === "C" ||
                      slot.statusSlot === "F"
                  ).length < 1 || loading
            }
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              t("meeting.confirmAgenda").toUpperCase()
            )}
          </Button>
        </div>
      </Grid>

      <span className={classes.titleModal}>{t("slots.statusSlot")} (S)</span>

      <div
        style={{
          padding: "10px",
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid gray",
        }}
      >
        <span>
          <LensIcon style={{ color: "green", fontSize: "small" }} />{" "}
          {statusMaterial[0]?.name.toUpperCase()}
        </span>
        <span>
          <LensIcon style={{ color: "gray", fontSize: "small" }} />{" "}
          {statusMaterial[1]?.name.toUpperCase()}{" "}
        </span>
        <span>
          <LensIcon style={{ color: "red", fontSize: "small" }} />{" "}
          {statusMaterial[2]?.name.toUpperCase()}{" "}
        </span>
        <span>
          <LensOutlinedIcon style={{ fontSize: "small" }} />{" "}
          {statusMaterial[3]?.name.toUpperCase()}
        </span>
      </div>

      <span className={classes.titleModal}>{t("slots.type")}</span>

      <div
        style={{
          padding: "10px",
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid gray",
        }}
      >
        <span>{`A - ${t("forms.committee.approval").toUpperCase()}`}</span>
        <span>{`I - ${t("forms.committee.information").toUpperCase()}`}</span>
        <span>{`D - ${t("discussion").toUpperCase()}`}</span>
      </div>
    </div>
  );
};

export default ConfirmScheduleModal;
